import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  output,
  viewChild,
  inject,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Nillable, valueOrDefault } from '@web/utils';
import { Accordion } from 'primeng/accordion';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import {
  AdminPaymentMethodFormData,
  AdminPaymentMethodFormGroup,
} from '../../models/admin-payment-method-form-data';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
} from '@web/shared/module/multimedia';
import {
  SummernoteComponent,
  SummernoteImageUploadFn,
} from '@web/shared/external';
import { ToggleButton } from 'primeng/togglebutton';

@Component({
  selector: 'app-admin-payment-method-form',
  imports: [
    ReactiveFormsModule,
    FormControlComponent,
    Button,
    PrimengButtonDirective,
    InputText,
    PrimengInputTextDirective,
    MultimediaChooseOrUploadControlComponent,
    SummernoteComponent,
    ToggleButton,
  ],
  templateUrl: './admin-payment-method-form.component.html',
  styleUrl: './admin-payment-method-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPaymentMethodFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);

  public readonly data = input<Nillable<AdminPaymentMethodFormData>>();
  public readonly uploadFn = input.required<SummernoteImageUploadFn>();
  public readonly submitClickEvent = output<AdminPaymentMethodFormData>();

  protected readonly accordion = viewChild(Accordion);
  protected readonly form = this.buildForm();

  protected activePriceIndex = 0;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminPaymentMethodFormData {
    const data = this.form.getRawValue();

    return new AdminPaymentMethodFormData({
      previewFile: valueOrDefault(data.previewFile, null),
      isActive: valueOrDefault(data.isActive, false),
      name: valueOrDefault(data.name, ''),
      description: valueOrDefault(data.description, ''),
    });
  }

  private buildForm(): AdminPaymentMethodFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      description: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminPaymentMethodFormData.DescriptionMaxLength)]),
      isActive: this.builder.control<Nillable<boolean>>(null),
      name: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminPaymentMethodFormData.NameMaxLength)]),
      previewFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue(
      {
        name: data?.name,
        isActive: data?.isActive,
        previewFile: data?.previewFile,
        description: data?.description,
      },
      { emitEvent: false },
    );
  }
}
